/**
 * Copyright (c) 2023-present,
 * Eunoia Digital, S.L. All rights reserved.
 */

import { AxiosError, AxiosResponse } from 'axios';
import configuration from 'config';
import { ConfiguracionMatcherResponse } from 'types/interfaces/idConfiguracionMatcher';
import ConfiguracionMatcher from 'types/models/ConfiguracionMatcher';
import apiService from './ApiService';

type PostConfiguracionMatcher = {
  nombre?: string;
  fechaInicio?: string;
  fechaFin?: string;
  idComunidad?: number;
  betaMax?: number;
  betaMin?: number;
  minimizarMiembros?: string;
  maximizarMiembros?: string;
  metodoDeCalculos?: string;
  step?: number;
};

export type DuplicarConfiguracionMatcher = {
  nombre: string;
};

export class MatcherConfiguracionService {
  private readonly http = apiService;

  private apiUrl = configuration.apiUrl;

  public async getAllConfiguracionesMatcher(): Promise<Array<ConfiguracionMatcher>> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/`;

    const response: AxiosResponse = await this.http.get(url);
    const configuraciones = new ConfiguracionMatcher().deserializeArray(response.data.datos);
    return configuraciones;
  }

  public async getConfiguracionMatcherById(id: Number): Promise<ConfiguracionMatcher> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}`;

    const response: AxiosResponse = await this.http.get(url);
    return new ConfiguracionMatcher().deserialize(response.data.datos);
  }

  async postConfiguracionMatcher(): Promise<ConfiguracionMatcherResponse> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/`;

    const response: AxiosResponse<ConfiguracionMatcherResponse> = await this.http.post(url);

    return response.data;
  }

  async duplicarConfiguracionMatcher(
    id: number,
    duplicarConfiguracionMatcher: DuplicarConfiguracionMatcher,
  ): Promise<ConfiguracionMatcherResponse> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/duplicar/${id}/`;

    const response: AxiosResponse<ConfiguracionMatcherResponse> = await this.http.post(
      url,
      duplicarConfiguracionMatcher,
    );

    return response.data;
  }

  async patchConfiguracionMatcher(id: number, patchConfiguracionMatcher: PostConfiguracionMatcher): Promise<string> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/`;

    const response: AxiosResponse<string> = await this.http.patch(url, patchConfiguracionMatcher);

    return response.data;
  }

  async deleteConfiguracionMatcher(id: number): Promise<unknown> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}`;

    const response: AxiosResponse<number> = await this.http.delete(url);

    return response;
  }

  // TODO
  async subirCsvConfiguracionMatcher(id: number, file: File): Promise<any> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/curva-generacion`;
    const csvData = new window.FormData();
    csvData.append('curvaDeGeneracion', file);

    let responsePromise;
    try {
      const response: AxiosResponse = await this.http.patch(url, csvData);
      responsePromise = response.status;
    } catch (error: unknown) {
      if ((error as AxiosError)?.response?.data && (error as AxiosError).response?.status === 400) {
        responsePromise = {
          codigo: (error as AxiosError).response?.data[0].codigo,
          mensaje: (error as AxiosError).response?.data[0].mensaje,
        };
      }
    }
    return responsePromise;
  }

  // Devuelve un CSV (representado por Blob en JavaScript)
  public async descargarInforme(id: number): Promise<Blob> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/curva-generacion/`;
    const response: AxiosResponse<Blob> = await this.http.get(url);

    return response.data;
  }
}

const MatcherConfiguracionInstance = new MatcherConfiguracionService();
export default MatcherConfiguracionInstance;
